import {useTraduire} from '../i18n/langue';
import {T} from '../i18n/textes';
import {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {laDateCourte, laDateEtHeure} from '../utils/dates';
import {
  ALL_TAGS,
  ALL_TEAMS,
  demandLabel,
  FeedbackReport,
  FeedbackStatus,
  FeedbackTag,
  FeedbackTeam,
  PLATFORM_DISPLAY_NAMES,
  PLATFORM_MAP,
  STATUS_COLORS,
  STATUS_LABELS,
  TAG_COLORS,
  TAG_COLORS_SELECTED,
  TAG_LABELS,
  TEAM_COLORS,
  TEAM_LABELS,
} from '../types/feedback';
import {AiAnalysisPanel} from './AiAnalysisPanel';
import {ConversationSection} from './ConversationSection';
import {UserHistorySection} from './UserHistorySection';
import {checkSilentClose} from '../services/feedbackService';
import {purchaseBackOfficeUrl} from '../services/purchaseBackOffice';
import {useUserInfo, useUserPurchases} from '../hooks/useFeedback';
import {useTeamMembers} from '../hooks/useTeamMembers';
import {useAppVersions} from '../hooks/useAppVersions';
import {isVersionOutdated} from '../services/appVersionsService';
import {Bug as BugIcon, Check, Crown, ShoppingCart} from '@phosphor-icons/react';
import {TopicIcon} from './TopicIcon';
import {getBug} from '../services/bugService';
import {BUG_STATUS_COLORS, BUG_STATUS_LABELS} from '../types/bug';
import type {Bug} from '../types/bug';
import {dashboardPath} from '../routes';

const PRIMARY_COLOR = '#3ab3ff';

// Provider icons/labels
const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'apple.com': 'Apple',
  'password': 'Email/Password',
  'phone': 'Phone',
};

interface FeedbackDetailProps {
  feedback: FeedbackReport;
  onUpdate: (updates: {
    status?: FeedbackStatus;
      team?: FeedbackTeam | null;
    internalNote?: string;
    assignee?: string;
      tags?: FeedbackTag[];
  }) => Promise<void>;
  saving: boolean;
  adminEmail?: string;
  adminName?: string;
}

export function FeedbackDetail({ feedback, onUpdate, saving, adminEmail, adminName }: FeedbackDetailProps) {
    const t = useTraduire();
  const [status, setStatus] = useState<FeedbackStatus>(feedback.status || 'new');
    const [team, setTeam] = useState<FeedbackTeam | ''>(feedback.team || '');
  const [internalNote, setInternalNote] = useState(feedback.internalNote || '');
  const [assignee, setAssignee] = useState(feedback.assignee || '');
    const [selectedTags, setSelectedTags] = useState<FeedbackTag[]>(feedback.tags || []);
  const [showScreenshot, setShowScreenshot] = useState(false);
  // Closing without replying stays the team's call; we only flag the cases a
  // human would regret closing in silence.
  const [silentClose, setSilentClose] =
    useState<{unanswered: boolean; looksImportant: boolean} | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSilentClose(null);
    checkSilentClose(feedback.id, 'ignored')
      .then((warning) => {
        if (!cancelled) setSilentClose(warning);
      })
      // On error, say nothing rather than warn wrongly.
      .catch(() => {
        if (!cancelled) setSilentClose(null);
      });
    return () => {
      cancelled = true;
    };
  }, [feedback.id]);

  const warnSilentClose = status === 'ignored' && silentClose !== null;

  // Bug auquel ce signalement est rattache (volet 1c).
  const [linkedBug, setLinkedBug] = useState<Bug | null>(null);
  useEffect(() => {
    if (!feedback.bugId) {
      setLinkedBug(null);
      return;
    }
    let cancelled = false;
    getBug(feedback.bugId).then((b) => {
      if (!cancelled) setLinkedBug(b);
    });
    return () => {
      cancelled = true;
    };
  }, [feedback.bugId]);

  const { purchases, loading: purchasesLoading } = useUserPurchases(feedback.uid);
  const { userInfo, loading: userInfoLoading } = useUserInfo(feedback.uid);
    // Load all team members (not filtered by team) so any member can be assigned
    const {members: allTeamMembers, loading: teamMembersLoading} = useTeamMembers();
    const {versions: appVersions, loading: versionsLoading} = useAppVersions();

    // Get platform key and latest version
    const platformKey = PLATFORM_MAP[feedback.platform];
    const latestVersion = platformKey && appVersions ? appVersions[platformKey] : null;
    const isOutdated = latestVersion && feedback.appVersion
        ? isVersionOutdated(feedback.appVersion, latestVersion)
        : false;

    // Group members by team for the dropdown
    const membersByTeam = allTeamMembers.reduce((acc, member) => {
        if (!acc[member.team]) {
            acc[member.team] = [];
        }
        acc[member.team].push(member);
        return acc;
    }, {} as Record<FeedbackTeam, typeof allTeamMembers>);

  const handleSave = async () => {
      await onUpdate({
          status,
          team: team || null,
          internalNote,
          assignee,
          tags: selectedTags,
      });
  };

    const toggleTag = (tag: FeedbackTag) => {
        setSelectedTags((prev) =>
            prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
        );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start mb-4">
          {/* Tout sur une seule ligne, qui se replie quand la place manque :
              l'etat, le sujet, l'equipe, puis les etiquettes. Un trait avant
              celles-ci, parce qu'elles ne sont pas de meme nature — les
              premieres decrivent la demande, les secondes ce que l'equipe en
              a fait. */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${
                STATUS_COLORS[feedback.status || 'new']
              }`}
            >
              {t(STATUS_LABELS[feedback.status || 'new'])}
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full bg-gray-100 text-gray-800">
              <TopicIcon topic={feedback.topic} size={16}/>
              {t(demandLabel(feedback))}
            </span>
            {feedback.team && (
                <span
                    className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${TEAM_COLORS[feedback.team]}`}>
                {t(TEAM_LABELS[feedback.team])}
              </span>
            )}
            {feedback.tags && feedback.tags.length > 0 && (
                <span className="h-4 w-px bg-gray-300" aria-hidden="true"/>
            )}
            {/* Le repli sur l'identifiant brut, comme dans la liste : sans
                lui, un tag que le back-office ne connait pas encore devenait
                une pastille vide et incolore — invisible, alors qu'elle porte
                justement ce qu'on ne sait pas encore lire. */}
            {feedback.tags?.map((tag) => (
                <span
                    key={tag}
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${TAG_COLORS[tag] || 'bg-gray-100 text-gray-600'}`}
                >
                {t(TAG_LABELS[tag]) || tag}
              </span>
            ))}
          </div>
          <div className="text-sm text-gray-500">
            {feedback.createdAt?.toDate
              ? laDateEtHeure(feedback.createdAt.toDate())
              : '-'}
          </div>
        </div>

        {/* Le titre ne s'affiche qu'avec son message : un achat non trouve
            n'en exige plus, et « Message » seul au-dessus du vide donnait a
            lire une absence. */}
        {feedback.message?.trim() && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">{t(T.message)}</h3>
            <p className="text-gray-900 whitespace-pre-wrap">{feedback.message}</p>
          </div>
        )}

        {/* Screenshot */}
        {feedback.screenshotUrl && (
          <div className="mb-6">
            <h3 className="text-sm font-medium text-gray-700 mb-2">{t(T.capture)}</h3>
            <button
              onClick={() => setShowScreenshot(!showScreenshot)}
              className="text-primary hover:text-primary-dark text-sm"
            >
              {showScreenshot ? t(T.masquerLaCapture) : t(T.montrerLaCapture)}
            </button>
            {showScreenshot && (
              <div className="mt-4">
                <img
                  src={feedback.screenshotUrl}
                  alt="Feedback screenshot"
                  className="max-w-full max-h-96 rounded-lg border border-gray-200 shadow-sm"
                />
              </div>
            )}
          </div>
        )}

        {/* Bug rattache (volet 1c) */}
        {linkedBug && (
          <div className="flex items-center gap-2 p-2 mt-4 bg-rose-50 rounded-lg">
            <BugIcon size={14} weight="regular" className="text-rose-600 flex-shrink-0"/>
            <span className="text-xs text-gray-800 flex-1 truncate">{linkedBug.title}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${BUG_STATUS_COLORS[linkedBug.status]}`}>
              {BUG_STATUS_LABELS[linkedBug.status]}
            </span>
            {linkedBug.fixVersion && (
              <span className="text-xs text-gray-500">v{linkedBug.fixVersion}</span>
            )}
            {linkedBug.sentryUrl && (
              <a
                href={linkedBug.sentryUrl}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-primary hover:underline flex-shrink-0"
              >
                {linkedBug.sentryShortId || 'Sentry'}
              </a>
            )}
          </div>
        )}

      </div>

      {/* AI Analysis */}
      <AiAnalysisPanel feedback={feedback} />

      {/* User Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t(T.infosUtilisateur)}</h3>
        {userInfoLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            <span className="text-sm">{t(T.chargementUtilisateur)}</span>
          </div>
        ) : userInfo ? (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.nomAffiche)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {userInfo.displayName || <span className="text-gray-400 italic">{t(T.nonRenseigne)}</span>}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.adresseEmail)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {userInfo.email ? (
                    <a href={`mailto:${userInfo.email}`} className="text-primary hover:underline">
                    {userInfo.email}
                  </a>
                ) : (
                  <span className="text-gray-400 italic">{t(T.nonRenseigne)}</span>
                )}
                {userInfo.emailVerified && (
                    <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Verified
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.identifiantUtilisateur)}</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono text-xs">
                    {feedback.uid}
                    <Link
                        to={dashboardPath(feedback.uid)}
                        className="ml-2 text-primary hover:text-primary-dark font-sans text-xs"
                    >
                        {t(T.voirToutesSesDemandes)}
                    </Link>
                </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.moyensDeConnexion)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {userInfo.providerIds.map((p) => PROVIDER_LABELS[p] || p).join(', ') || '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.compteCree)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {userInfo.creationTime ? laDateCourte(new Date(userInfo.creationTime)) : '-'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.derniereConnexion)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {userInfo.lastSignInTime ? laDateEtHeure(new Date(userInfo.lastSignInTime)) : '-'}
              </dd>
            </div>
          </dl>
        ) : (
          <p className="text-sm text-gray-500 italic">{t(T.utilisateurIntrouvable)}</p>
        )}
      </div>

      {/* Device Info */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t(T.infosAppareil)}</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-gray-500">{t(T.plateforme)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                  {platformKey ? PLATFORM_DISPLAY_NAMES[platformKey] : feedback.platform}
              </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t(T.versionApplication)}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              <span className={isOutdated ? 'text-orange-600 font-medium' : ''}>
                {feedback.appVersion}
              </span>
                <span className="text-gray-500"> ({feedback.buildNumber})</span>
                {versionsLoading ? (
                    <span className="ml-2 text-xs text-gray-400">{t(T.chargement)}</span>
                ) : latestVersion ? (
                    <span className="ml-2">
                  {isOutdated ? (
                      <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      Outdated (latest: {latestVersion})
                    </span>
                  ) : (
                      <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Up to date
                    </span>
                  )}
                </span>
                ) : platformKey ? (
                    <span className="ml-2 text-xs text-gray-400">{t(T.versionLaPlusRecenteInconnue)}</span>
                ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t(T.langue)}</dt>
            <dd className="mt-1 text-sm text-gray-900">{feedback.locale}</dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t(T.ecranOuvert)}</dt>
            <dd className="mt-1 text-sm text-gray-900 font-mono">
              {feedback.currentRoute}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500">{t(T.modeBeta)}</dt>
            <dd className="mt-1 text-sm text-gray-900">
              {feedback.betaEnabled ? 'Yes' : 'No'}
            </dd>
          </div>
          {/* Message venu du formulaire web : ce qui precede decrit le
              navigateur, pas l'installation. Le dire evite de lire « App
              Version » vide comme une anomalie. */}
          {feedback.source === 'web' && (
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.origine)}</dt>
              <dd className="mt-1 text-sm text-gray-900">
                Formulaire web — infos techniques du navigateur
              </dd>
            </div>
          )}

          {/* Le parcours de saisie, quelle que soit la porte d'entree : le
              formulaire du site et celui de l'application posent les memes
              questions et ecrivent les memes champs. Les reserver au web
              cachait tout ce que l'application envoie. */}
          {feedback.topic && (
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.sujet)}</dt>
              <dd className="mt-1 text-sm text-gray-900">{feedback.topic}</dd>
            </div>
          )}
          {/* Ce qu'un e-mail apporte et que le formulaire n'a pas : son objet,
              le nom de l'expediteur, et surtout la mention des pieces
              jointes. Leur contenu reste dans la boite — sans cette ligne,
              rien a l'ecran ne dirait qu'un recu ou une capture attend d'y
              etre ouvert. */}
          {feedback.source === 'email' && feedback.subject && (
            <div>
              <dt className="text-sm font-medium text-gray-500">{t(T.objetDuMessage)}</dt>
              <dd className="mt-1 text-sm text-gray-900">{feedback.subject}</dd>
            </div>
          )}
          {feedback.attachmentNames && feedback.attachmentNames.length > 0 && (
            <div>
              <dt className="text-sm font-medium text-gray-500">
                Pièces jointes ({feedback.attachmentNames.length})
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                <ul className="space-y-0.5">
                  {feedback.attachmentNames.map((nom) => (
                    <li key={nom} className="font-mono text-xs text-gray-700">{nom}</li>
                  ))}
                </ul>
                <p className="mt-1 text-xs text-gray-500">
                  Non rapatriées : ouvrez le message dans la boîte support@assimil.app.
                </p>
              </dd>
            </div>
          )}
          <>
              {feedback.purchaseChannel && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.canalDachatDeclare)}</dt>
                  <dd className="mt-1 text-sm text-gray-900">{feedback.purchaseChannel}</dd>
                </div>
              )}
              {(feedback.source === 'web' || feedback.source === 'email') && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.emailDeContact)}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {feedback.contactEmail ? (
                      <a href={`mailto:${feedback.contactEmail}`} className="text-primary hover:underline">
                        {feedback.contactEmail}
                      </a>
                    ) : (
                      <span className="text-orange-600">{t(T.emailAbsent)}</span>
                    )}
                  </dd>
                </div>
              )}
              <div>
                <dt className="text-sm font-medium text-gray-500">{t(T.methodeDeclaree)}</dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {feedback.method || feedback.lesson || feedback.section ? (
                    [
                      feedback.method,
                      feedback.lesson ? `leçon ${feedback.lesson}` : null,
                      feedback.section,
                    ].filter(Boolean).join(' — ')
                  ) : (
                    <span className="text-gray-400">{t(T.nonPrecisee)}</span>
                  )}
                </dd>
                {/* L'EAN identifie la methode sans ambiguite, la ou le titre
                    confond deux editions. Absent quand la personne a tape son
                    texte au lieu de choisir dans la liste. */}
                {feedback.methodEan && (
                  <dd className="mt-0.5 text-xs text-gray-500 font-mono">
                    EAN {feedback.methodEan}
                  </dd>
                )}
              </div>
              {feedback.orderNumber && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.numeroDeCommande)}</dt>
                  <dd className="mt-1 text-sm text-gray-900 font-mono">{feedback.orderNumber}</dd>
                </div>
              )}
              {/* Ce que la recherche automatique a trouve, pour ne pas la
                  refaire a la main — et pour savoir ce que la personne a deja
                  recu par e-mail. */}
              {feedback.diagnosticResult && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.rechercheAutomatique)}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {feedback.diagnosticResult === 'none' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800">
                        aucun achat trouvé sur cette adresse
                      </span>
                    ) : feedback.diagnosticResult === 'found' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                        {feedback.diagnosticPurchases} achat(s) trouvé(s)
                      </span>
                    ) : feedback.diagnosticResult === 'pending' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-800">
                        achat en attente d'activation
                      </span>
                    ) : (
                      <span className="text-gray-500">{t(T.rechercheEnEchec)}</span>
                    )}
                  </dd>
                </div>
              )}
              {/* Ce que la personne a repondu depuis l'e-mail. « Oui » a
                  ferme la demande de lui-meme ; « non » dit qu'elle attend
                  toujours, ce qu'une demande ouverte ne disait pas. */}
              {feedback.resolvedAnswer && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.retrouveDepuisEmail)}</dt>
                  <dd className="mt-1 text-sm">
                    {feedback.resolvedAnswer === 'oui' ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                        oui — la personne a clos elle-même
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-orange-100 text-orange-800">
                        non — elle attend toujours
                      </span>
                    )}
                  </dd>
                </div>
              )}
              {feedback.relatedFeedbackId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.completeLaDemande)}</dt>
                  <dd className="mt-1 text-sm">
                    <a href={`/feedback/${feedback.relatedFeedbackId}`} className="text-primary hover:underline font-mono">
                      {feedback.relatedFeedbackId}
                    </a>
                  </dd>
                </div>
              )}
              {/* Le web demande a la personne si elle est a jour ; l'app le
                  sait d'elle-meme et l'ecrit dans `appVersion`. */}
              {feedback.source === 'web' && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t(T.aJour)}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {feedback.confirmedLatestVersion
                      ? `Confirmé par la personne${feedback.appVersion ? ` (${feedback.appVersion})` : ''}`
                      : <span className="text-orange-600">{t(T.nonConfirme)}</span>}
                  </dd>
                </div>
              )}
          </>
        </dl>
      </div>

      {/* User Purchases */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h3 className="text-lg font-medium text-gray-900">
            {t(T.achatsDuCompte)}
            {purchases.length > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">
                ({purchases.length})
              </span>
            )}
          </h3>
          {/* The order itself lives in the sales back-office: what shows here is
              only what the app knows, which is exactly what is in doubt when a
              customer says they paid and sees nothing. */}
          {userInfo?.email && (
            <a
              href={purchaseBackOfficeUrl({email: userInfo.email})}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-primary hover:text-primary-dark hover:underline whitespace-nowrap"
            >
              {t(T.verifierDansLeBackOffice)}
            </a>
          )}
        </div>
        {purchasesLoading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400"></div>
            <span className="text-sm">{t(T.chargementAchats)}</span>
          </div>
        ) : purchases.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No purchases found
            {userInfo?.email && ' — the order may still exist in the sales back-office'}
          </p>
        ) : (
          <ul className="space-y-3">
            {purchases.map((purchase) => (
              <li key={purchase.ean} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                <div className="flex items-start gap-3">
                  {purchase.coverUrl && (
                    <img
                      src={purchase.coverUrl}
                      alt=""
                      className="w-10 h-12 object-cover rounded shadow-sm flex-shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-gray-900">{purchase.title}</span>
                      {/* Une icone avec le mot : la couronne dit l'essai, le
                          caddie dit l'achat. On les distingue du coin de
                          l'oeil,
                          sans lire — et sans dependre de la seule couleur,
                          que tout le monde ne distingue pas.

                          L'essai porte un orange petillant, choisi a l'oeil :
                          mesure a 2,7:1 sur son fond pale, sous le seuil AA.
                          Assume — comme le blanc sur le menthe. L'ecrire ici
                          evite qu'on le « corrige » un jour en croyant a un
                          oubli. */}
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${
                          purchase.isTrial
                            ? 'bg-[#fff3e8] text-[#ff6b00] border border-[#ffc59e]'
                            : 'bg-success/10 text-success-dark border border-success/30'
                        }`}
                      >
                        {purchase.isTrial
                          ? <Crown size={14} weight="regular" aria-hidden="true"/>
                          : <ShoppingCart size={14} weight="regular" aria-hidden="true"/>}
                        {purchase.licenseType}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400 font-mono mt-0.5">
                      {userInfo?.email ? (
                        <a
                          href={purchaseBackOfficeUrl({email: userInfo.email, ean: purchase.ean})}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-primary hover:underline"
                          title={t(T.trouverLaCommande)}
                        >
                          {purchase.ean} ↗
                        </a>
                      ) : (
                        purchase.ean
                      )}
                    </div>
                    {!purchase.isTrial && purchase.paymentDate && (
                      <div className="text-xs text-gray-500 mt-1">
                        <span className="font-medium">{t(T.achete)}</span>{' '}
                        {laDateCourte(purchase.paymentDate)}
                        {purchase.paymentPlatform && purchase.paymentPlatform !== 'Unknown' && (
                          <span className="ml-2">
                            via <span className="font-medium">{purchase.paymentPlatform}</span>
                          </span>
                        )}
                        {purchase.purchaseOrigin && purchase.purchaseOrigin !== 'Unknown' && (
                          <span className="ml-2 text-gray-400">({purchase.purchaseOrigin})</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Admin Actions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{t(T.actionsEquipe)}</h3>
        <div className="space-y-4">
          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as FeedbackStatus)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            >
              {Object.entries(STATUS_LABELS).map(([value, libelle]) => (
                <option key={value} value={value}>
                  {t(libelle)}
                </option>
              ))}
            </select>
            {warnSilentClose && (
              <p
                className={`mt-1 text-xs ${
                  silentClose!.looksImportant ? 'text-red-700' : 'text-amber-700'
                }`}
              >
                {silentClose!.looksImportant
                  ? 'Heads-up: nobody has replied here, and this message mentions a payment or an access problem. Closing it is still your call — this is only a reminder in case it slipped through.'
                  : 'Heads-up: no reply was ever posted in this thread. Fine if the comment needs none, or if you already answered by phone or e-mail.'}
              </p>
            )}
          </div>

            {/* Team */}
            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team
                </label>
                <select
                    value={team}
                    onChange={(e) => setTeam(e.target.value as FeedbackTeam | '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                    <option value="">{t(T.aucuneEquipe)}</option>
                    {ALL_TEAMS.map((equipe) => (
                        <option key={equipe} value={equipe}>
                            {t(TEAM_LABELS[equipe])}
                        </option>
                    ))}
                </select>
            </div>

          {/* Assignee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(T.responsable)}
            </label>
              <select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              disabled={teamMembersLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100"
              >
                  <option value="">{t(T.aucunResponsable)}</option>
                  {ALL_TEAMS.map((teamKey) => {
                      const members = membersByTeam[teamKey];
                      if (!members || members.length === 0) return null;
                      return (
                          <optgroup key={teamKey} label={t(TEAM_LABELS[teamKey])}>
                              {members.map((member) => (
                                  <option key={member.id} value={member.email}>
                                      {member.name}
                                  </option>
                              ))}
                          </optgroup>
                      );
                  })}
              </select>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
            </label>
              <div className="flex flex-wrap gap-2">
                  {ALL_TAGS.map((tag) => (
                      // L'etiquette retenue se remplit de sa propre couleur,
                      // texte en blanc : le plein dit « choisi » sans ajouter
                      // ni trait ni halo, la ou la bordure doublait le
                      // contour de la pastille. La coche acheve de le dire a
                      // qui ne distingue pas les nuances, et `aria-pressed`
                      // aux lecteurs d'ecran, qui ne voient ni l'un ni
                      // l'autre.
                      <button
                          key={tag}
                          type="button"
                          onClick={() => toggleTag(tag)}
                          aria-pressed={selectedTags.includes(tag)}
                          className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-full border transition-all ${
                              selectedTags.includes(tag)
                                  ? `${TAG_COLORS_SELECTED[tag]} border-transparent shadow-sm`
                                  : 'bg-primary/[0.03] text-primary/70 border-primary/15 hover:border-primary/30'
                          }`}
                      >
                          {t(TAG_LABELS[tag])}
                          {selectedTags.includes(tag) && (
                              <Check size={14} weight="bold" className="shrink-0"/>
                          )}
                      </button>
                  ))}
              </div>
          </div>

          {/* Internal Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t(T.noteInterne)}
            </label>
            <textarea
              value={internalNote}
              onChange={(e) => setInternalNote(e.target.value)}
              rows={4}
              placeholder={t(T.notesInternes)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
            />
          </div>

          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2 px-4 border border-transparent rounded-pill shadow-bo text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-colors"
            style={{backgroundColor: PRIMARY_COLOR}}
          >
            {saving ? t(T.enregistrement) : t(T.enregistrerLesChangements)}
          </button>

          {feedback.updatedAt && feedback.updatedBy && (
            <p className="text-sm text-gray-500 text-center">
              Last updated by {feedback.updatedBy} on{' '}
              {laDateEtHeure(feedback.updatedAt.toDate())}
            </p>
          )}
        </div>
      </div>

      {/* Other feedbacks from the same user */}
      <UserHistorySection uid={feedback.uid} excludeId={feedback.id}/>

      {/* Conversation Section */}
      {adminEmail && (
        <ConversationSection
          feedbackId={feedback.id}
          originalMessage={feedback.message}
          originalCreatedAt={feedback.createdAt?.toDate ? feedback.createdAt.toDate() : new Date()}
          adminEmail={adminEmail}
          adminName={adminName}
        />
      )}

    </div>
  );
}
